<script setup lang="ts">
import { ref } from 'vue';
import type { ExportPageLayout } from '../../types/export';
import { exportPageFormats } from '../../lib/exportPageLayout';

const props = defineProps<{ layout: ExportPageLayout; busy: boolean }>();
const emit = defineEmits<{ update: [layout: ExportPageLayout] }>();
const preset = ref<string>(
  exportPageFormats.find(
    (format) => format.widthMm === props.layout.widthMm && format.heightMm === props.layout.heightMm
  )?.key ?? 'custom'
);
const sides = [
  { key: 'top', label: 'Oben' },
  { key: 'right', label: 'Rechts' },
  { key: 'bottom', label: 'Unten' },
  { key: 'left', label: 'Links' }
] as const;
function chooseFormat() {
  const format = exportPageFormats.find((item) => item.key === preset.value);
  if (format) emit('update', { ...props.layout, widthMm: format.widthMm, heightMm: format.heightMm });
}
function dimension(key: 'widthMm' | 'heightMm', value: string) {
  preset.value = 'custom';
  emit('update', { ...props.layout, [key]: value === '' ? NaN : Number(value) });
}
function margin(key: keyof ExportPageLayout['marginsMm'], value: string) {
  emit('update', {
    ...props.layout,
    marginsMm: { ...props.layout.marginsMm, [key]: value === '' ? NaN : Number(value) }
  });
}
</script>

<template>
  <section class="export-page-settings" aria-label="Seitenformat und Ränder">
    <h3>Seitenformat und Ränder</h3>
    <label
      >Format<select v-model="preset" aria-label="Format" :disabled="busy" @change="chooseFormat">
        <option v-for="format in exportPageFormats" :key="format.key" :value="format.key">{{ format.label }}</option>
        <option value="custom">Eigene Maße</option>
      </select></label
    >
    <div class="export-page-dimensions">
      <label
        >Breite (mm)<input
          type="number"
          required
          min="80"
          max="600"
          step="any"
          :value="layout.widthMm"
          :disabled="busy"
          @input="dimension('widthMm', ($event.target as HTMLInputElement).value)"
      /></label>
      <label
        >Höhe (mm)<input
          type="number"
          required
          min="80"
          max="600"
          step="any"
          :value="layout.heightMm"
          :disabled="busy"
          @input="dimension('heightMm', ($event.target as HTMLInputElement).value)"
      /></label>
    </div>
    <div class="export-page-margins">
      <label v-for="side in sides" :key="side.key"
        >Rand {{ side.label.toLocaleLowerCase('de-DE') }} (mm)<input
          type="number"
          required
          min="0"
          max="600"
          step="any"
          :value="layout.marginsMm[side.key]"
          :disabled="busy"
          @input="margin(side.key, ($event.target as HTMLInputElement).value)"
      /></label>
    </div>
    <small>Breite × Höhe und alle vier Seitenränder sind frei anpassbar. Die Ränder gelten auf jeder Seite.</small>
  </section>
</template>

<style scoped>
.export-page-settings {
  display: grid;
  gap: 10px;
  margin-bottom: 18px;
}
h3 {
  margin: 0;
  font-size: 14px;
}
label {
  display: grid;
  gap: 4px;
  font-size: 12px;
  color: var(--ui-text-muted);
}
select,
input {
  width: 100%;
  min-width: 0;
}
.export-page-dimensions,
.export-page-margins {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
small {
  color: var(--ui-text-muted);
  line-height: 1.5;
}
@media (min-width: 700px) {
  .export-page-margins {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
